import axios from "axios";
import BASEURL from "./pages/Utils/Baseurl";

// Base URL for the API — configured via REACT_APP_API_URL in .env
export const BASE_URL = BASEURL;

// Create Axios client
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request Interceptor (Add token dynamically)
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor (Handle token expiration)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = sessionStorage.getItem("refreshToken");
        const response = await axios.post(`${BASE_URL}admin/refresh-token`, {
          refreshToken,
        });

        const newAccessToken = response.data.accessToken;
        sessionStorage.setItem("token", newAccessToken);
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (err) {
        // SEC-06: Clear all sensitive session data on auth failure
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("is_auth");
        sessionStorage.removeItem("user");
        window.location.href = "/sign-in";
        return Promise.reject(err);
      }
    }
    return Promise.reject(error);
  }
);

// Generic GraphQL request handler
export const sendRequest = async (
  query,
  variables = {},
  isFileUpload = false
) => {
  try {
    if (isFileUpload) {
      const formData = new FormData();
      formData.append("operations", JSON.stringify({ query, variables }));
      formData.append("map", JSON.stringify({ 0: ["variables.file"] }));
      formData.append("0", variables.file);

      const response = await apiClient.post("", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.errors) {
        throw new Error(
          response.data.errors.map((err) => err.message).join(", ")
        );
      }

      return response.data.data;
    } else {
      const response = await apiClient.post("", { query, variables });
      if (response.data.errors) {
        throw new Error(
          response.data.errors.map((err) => err.message).join(", ")
        );
      }
      return response.data.data;
    }
  } catch (error) {
    console.error("API Request Error:", error);
    throw error;
  }
};

// Dynamic methods for any entity

// Fetch all entities with dynamic fields
export const fetchAll = async (entityName, fields) => {
  const query = `
    query all${entityName} {
      all${entityName} {
        ${fields.join("\n")}
      }
    }
  `;
  return await sendRequest(query);
};
// Fetch all entities with dynamic fields
export const fetchCompanyAll = async (entityName, fields) => {
  const query = `
    query ${entityName.toLowerCase()}byall {
      ${entityName} {
        ${fields.join("\n")}
      }
    }
  `;
  return await sendRequest(query);
};

// Fetch an entity by ID with dynamic fields
export const fetchById = async (entityName, id, fields) => {
  const query = `
    query Get${entityName}ById($id: String!) {
      get${entityName}ById(id: $id) {
        ${fields.join("\n")}
      }
    }
  `;
  const variables = { id };
  return await sendRequest(query, variables);
};

// Create a new entity with dynamic fields
export const createItem = async (entityName, input, fields) => {
  const mutation = `
    mutation Create${entityName}($input: ${entityName}Input!) {
      create${entityName}(input: $input) {
        ${entityName.toLowerCase()} {
          ${fields.join("\n")}
        }
      }
    }
  `;
  const variables = { input };
  return await sendRequest(mutation, variables);
};

// Update an entity with dynamic fields
export const updateItem = async (entityName, id, input, fields) => {
  const mutation = `
    mutation Update${entityName}($id: ID!, $input: ${entityName}Input!) {
      update${entityName}(id: $id, input: $input) {
        ${entityName.toLowerCase()} {
          ${fields.join("\n")}
        }
      }
    }
  `;
  const variables = { id, input };
  return await sendRequest(mutation, variables);
};

// Delete an entity
export const deleteItem = async (entityName, id) => {
  const mutation = `
    mutation Delete${entityName}($id: ID!) {
      delete${entityName}(id: $id) {
        success
        message
      }
    }
  `;
  const variables = { id };
  return await sendRequest(mutation, variables);
};
// add a new entity with dynamic fields
export const addItem = async (entityName, input, fields, file = null) => {
  const mutation = `
    mutation Add${entityName}($${entityName.toLowerCase()}Data: ${entityName}Input!,$logo: Upload) {
      add${entityName}(${entityName.toLowerCase()}Data: $${entityName.toLowerCase()}Data, logo: $logo) {
        ${entityName.toLowerCase()} {
          ${fields.join("\n")}
        }
      }
    }
  `;
  const variables = file ? { input, file } : { input };
  return await sendRequest(mutation, variables, !!file);
};

export const editItem = async (entityName, id, input, fields, file = null) => {
  const mutation = `
    mutation Update${entityName}($id: Int!, $${entityName.toLowerCase()}Data: ${entityName}Input!, $file: Upload) {
      update${entityName}(id: $id, companyData: $${entityName.toLowerCase()}Data, file: $file) {
        ${entityName.toLowerCase()} {
          ${fields.join("\n")}
        }
      }
    }
  `;
  const variables = file ? { id, input, file } : { id, input };
  return await sendRequest(mutation, variables, !!file);
};
